import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GameEngine, GameState, GameStatus, Tile, getValidMatches, findValidPair } from '@snap-pairs/game-core';
import GameCanvas from './GameCanvas';
import MenuButton from './MenuButton';
import HowToPlayModal from './HowToPlayModal';
import AboutModal from './AboutModal';
import TermsOfServiceModal from './TermsOfServiceModal';
import LeaderboardModal from './LeaderboardModal';
import LevelModal from './LevelModal';
import SplashPage from './SplashPage';
import { useAuth } from '../hooks/useAuth';
import { leaderboardAPI } from '../utils/api';
import { soundManager } from '../utils/sounds';
import { loadPreferences, savePreferences, getDefaultPreferences } from '../utils/cookies';
import './Game.css';

const Game: React.FC = () => {
  const savedPrefs = loadPreferences() || getDefaultPreferences();
  const [infinityMode, setInfinityMode] = useState(savedPrefs.isInfinityMode);
  const [gridSize, setGridSize] = useState<4 | 6 | 8>(savedPrefs.gridSize);
  const [engine] = useState(() => new GameEngine({
    gridSize: gridSize,
    roundDuration: infinityMode ? Infinity : 60,
    enableRegeneration: infinityMode
  }));
  const [gameState, setGameState] = useState<GameState>(engine.getState());
  const [validMatches, setValidMatches] = useState<Tile[]>([]);

  const [hintTiles, setHintTiles] = useState<Tile[]>([]);
  const [isMuted, setIsMuted] = useState(savedPrefs.isMuted);
  const [isDarkMode, setIsDarkMode] = useState(savedPrefs.isDarkMode);
  const [showShuffleNotification, setShowShuffleNotification] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showLevel, setShowLevel] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [playAsGuest, setPlayAsGuest] = useState(false);

  const { user, isAuthenticated, isLoading, signInWithGoogle, signOut } = useAuth();

  const prevMatchCountRef = useRef(0);
  const prevScoreRef = useRef(0);

  useEffect(() => {
    soundManager.setMuted(isMuted);
  }, []);

  useEffect(() => {
    engine.onShuffle(() => {
      setIsShuffling(true);
      setShowShuffleNotification(true);
      setTimeout(() => {
        setIsShuffling(false);
        setShowShuffleNotification(false);
      }, 1000);
    });

    engine.onUpdate((state) => {
      const stats = engine.getStats();

      if (stats.matchCount > prevMatchCountRef.current) {
        const scoreIncrease = state.score - prevScoreRef.current;
        if (scoreIncrease > 100) {
          soundManager.playCombo();
        } else {
          soundManager.playMatch();
        }
        prevMatchCountRef.current = stats.matchCount;
      }

      if (state.status === GameStatus.Won) {
        soundManager.playWin();
      }

      prevScoreRef.current = state.score;
      setGameState(state);

      if (state.selectedTile) {
        const matches = getValidMatches(state.board, state.selectedTile);
        setValidMatches(matches);
      } else {
        setValidMatches([]);
      }
    });

    return () => {
      engine.destroy();
    };
  }, [engine]);


  useEffect(() => {
    savePreferences({
      isMuted,
      isDarkMode,
      isInfinityMode: infinityMode,
      gridSize
    });
  }, [isMuted, isDarkMode, infinityMode, gridSize]);

  const handleTileClick = useCallback((tile: Tile) => {
    soundManager.playClick();
    setHintTiles([]);
    engine.selectTile(tile);
  }, [engine]);

  const handleStart = () => {
    engine.start();
  };

  const handleReset = () => {
    engine.reset();
    setScoreSubmitted(false);
    setSubmissionError(null);
  };

  const handlePause = () => {
    if (gameState.status === GameStatus.Playing) {
      engine.pause();
    } else if (gameState.status === GameStatus.Paused) {
      engine.resume();
    }
  };

  const handleToggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    soundManager.setMuted(newMutedState);
  };

  const handleToggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleToggleInfinityMode = () => {
    const newMode = !infinityMode;
    setInfinityMode(newMode);
    // Note: Game needs to be restarted for mode change to take effect
    window.location.reload();
  };

  const handleLevelChange = (newLevel: 4 | 6 | 8) => {
    setGridSize(newLevel);
    // Save preferences and reload to apply new grid size
    savePreferences({
      isMuted,
      isDarkMode,
      isInfinityMode: infinityMode,
      gridSize: newLevel
    });
    window.location.reload();
  };

  const handleShowHint = () => {
    if (gameState.status !== GameStatus.Playing) return;

    // Apply hint penalty
    engine.applyPenalty(50);

    const pair = findValidPair(gameState.board);
    if (pair) {
      setHintTiles([pair[0], pair[1]]);
      // Clear hint after 3 seconds
      setTimeout(() => setHintTiles([]), 3000);
    } else {
      // No valid matches found, trigger shuffle
      engine.checkAndShuffleIfNeeded();
    }
  };

  const handleSubmitScore = async () => {
    if (!isAuthenticated || infinityMode || scoreSubmitted) {
      return;
    }

    setSubmissionError(null);
    try {
      // Get fresh stats at time of submission
      const currentStats = engine.getStats();

      // Calculate time bonus: 5 points per second remaining
      const timeRemaining = 60 - currentStats.timeElapsed;
      const timeBonus = Math.max(0, timeRemaining) * 5;
      const finalScore = gameState.score + timeBonus;

      console.log('Submitting score:', {
        baseScore: gameState.score,
        timeBonus,
        finalScore,
        match_count: currentStats.matchCount,
        time_elapsed: currentStats.timeElapsed,
        grid_size: gridSize
      });

      await leaderboardAPI.submitScore({
        score: finalScore,
        match_count: currentStats.matchCount,
        time_elapsed: currentStats.timeElapsed,
        grid_size: gridSize
      });
      setScoreSubmitted(true);
    } catch (error) {
      console.error('Submit error:', error);
      setSubmissionError(error instanceof Error ? error.message : 'Failed to submit score');
    }
  };

  // Auto-submit score when game is won
  useEffect(() => {
    if (gameState.status === GameStatus.Won && isAuthenticated && !infinityMode && !scoreSubmitted) {
      handleSubmitScore();
    }
  }, [gameState.status, isAuthenticated, infinityMode, scoreSubmitted]);

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) {
      return '∞';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusText = (): string => {
    switch (gameState.status) {
      case GameStatus.NotStarted:
        return 'Ready to Play';
      case GameStatus.Playing:
        return 'Playing';
      case GameStatus.Paused:
        return 'Paused';
      case GameStatus.GameOver:
        return 'Time\'s Up!';
      case GameStatus.Won:
        return 'You Won!';
      default:
        return '';
    }
  };

  const stats = engine.getStats();

  // Show splash page if not authenticated and not playing as guest
  if (!isAuthenticated && !isLoading && !playAsGuest) {
    return (
      <SplashPage
        onPlayAsGuest={() => setPlayAsGuest(true)}
        user={user}
        isLoading={isLoading}
        onSignIn={signInWithGoogle}
        onSignOut={signOut}
      />
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="game-container">
        <div style={{ textAlign: 'center', padding: '3rem', color: '#fff' }}>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
      />

      <AboutModal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        onOpenTerms={() => setShowTerms(true)}
      />

      <TermsOfServiceModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
      />

      <LeaderboardModal
        isOpen={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
        userId={user?.id}
        gridSize={gridSize}
      />

      <LevelModal
        isOpen={showLevel}
        onClose={() => setShowLevel(false)}
        selectedLevel={gridSize}
        onLevelChange={handleLevelChange}
      />

      <div className="game-info-container">
        <MenuButton
          isMuted={isMuted}
          isDarkMode={isDarkMode}
          isInfinityMode={infinityMode}
          onToggleMute={handleToggleMute}
          onToggleDarkMode={handleToggleDarkMode}
          onToggleInfinityMode={handleToggleInfinityMode}
          onShowHowToPlay={() => setShowHowToPlay(true)}
          onShowAbout={() => setShowAbout(true)}
          onShowLeaderboard={() => setShowLeaderboard(true)}
          onShowLevel={() => setShowLevel(true)}
          onSignOut={signOut}
        />
        <div className="game-info">
          <div className="info-row info-row-top">
            <div className="info-item">
              <span className="label">Time:</span>
              <span className="value time">{formatTime(gameState.timeRemaining)}</span>
            </div>
          </div>
          <div className="info-row info-row-bottom">
            <div className="info-item">
              <span className="label">Score:</span>
              <span className="value score">{gameState.score}</span>
            </div>
            <div className="info-item">
              <span className="label">Matches:</span>
              <span className="value">{stats.matchCount}</span>
            </div>
            <div className="info-item">
              <span className="label">Remaining:</span>
              <span className="value">{stats.remainingTiles}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="canvas-container">
        <GameCanvas
          board={gameState.board}
          selectedTile={gameState.selectedTile}
          validMatches={validMatches}
          hintTiles={hintTiles}
          onTileClick={handleTileClick}
          canvasSize={600}
          gameStatus={gameState.status}
          darkMode={isDarkMode}
          isShuffling={isShuffling}
        />
      </div>

      <div className="game-controls">
        {gameState.status === GameStatus.NotStarted && (
          <button className="btn btn-primary" onClick={handleStart}>
            Start Game
          </button>
        )}

        {(gameState.status === GameStatus.Playing || gameState.status === GameStatus.Paused) && (
          <>
            <button className="btn btn-secondary" onClick={handlePause}>
              {gameState.status === GameStatus.Playing ? 'Pause' : 'Resume'}
            </button>
            <button className="btn btn-secondary" onClick={handleReset}>
              Reset
            </button>
            {gameState.status === GameStatus.Playing && (
              <button className="btn btn-hint" onClick={handleShowHint}>
                💡 Show Hint (-50 pts)
              </button>
            )}
          </>
        )}

        {(gameState.status === GameStatus.GameOver || gameState.status === GameStatus.Won) && (
          <button className="btn btn-primary" onClick={handleReset}>
            Play Again
          </button>
        )}
      </div>

      {(gameState.status === GameStatus.GameOver || gameState.status === GameStatus.Won) && (
        <div className="game-over-modal">
          <div className="modal-content">
            <h2>{gameState.status === GameStatus.Won ? 'Congratulations!' : 'Game Over'}</h2>
            <div className="final-stats">
              <p>Final Score: <strong>{gameState.score}</strong></p>
              <p>Matches Made: <strong>{stats.matchCount}</strong></p>
              <p>Time Elapsed: <strong>{formatTime(stats.timeElapsed)}</strong></p>
            </div>

            {gameState.status === GameStatus.Won && !infinityMode && !isAuthenticated && (
              <p className="auth-prompt">Sign in to submit your score to the leaderboard!</p>
            )}

            {scoreSubmitted && (
              <p className="success-message">Score submitted to leaderboard!</p>
            )}

            {submissionError && (
              <p className="error-message">{submissionError}</p>
            )}

            <button className="btn btn-primary" onClick={handleReset}>
              Play Again
            </button>
          </div>
        </div>
      )}

      {showShuffleNotification && (
        <div className="shuffle-notification">
          <div className="shuffle-content">
            Tiles reshuffled
          </div>
        </div>
      )}
    </div>
  );
};

export default Game;
